/**
 * @fileoverview Key Generation Service - RSA key pair generation and management
 *
 * This service provides cryptographic key generation and management functionality
 * using the Web Crypto API. It generates RSA-OAEP key pairs suitable for hybrid
 * encryption schemes (combining RSA and AES encryption).
 *
 * **Key Features:**
 * - RSA-OAEP key pair generation (2048-bit, upgradeable to 4096-bit)
 * - PEM format import/export for storage and transmission
 * - Password-based private key encryption using PBKDF2 + AES-GCM
 * - Web Crypto API integration for hardware-accelerated operations
 *
 * **Cryptographic Standards:**
 * - Algorithm: RSA-OAEP (Optimal Asymmetric Encryption Padding)
 * - Key Size: 2048-bit (MVP), 4096-bit recommended for production
 * - Hash: SHA-256
 * - Public Exponent: 65537 (standard value)
 * - Private Key Protection: PBKDF2 (100,000 iterations) + AES-256-GCM
 *
 * **Key Lifecycle:**
 * 1. Generate key pair → generateKeyPair()
 * 2. Export to PEM format → exportKeyPair()
 * 3. Encrypt private key with password → encryptPrivateKey()
 * 4. Store encrypted keys → KeyStorageService.storeKeys()
 * 5. Later: Retrieve and decrypt → decryptPrivateKey()
 * 6. Import from PEM → importKeyPair()
 *
 * @module services/KeyGenerationService
 * @requires window.crypto.subtle (Web Crypto API)
 */

import { createLogger } from '@xanectech/lib';

const logger = createLogger('KeyGenerationService');

/**
 * Web Crypto API key pair structure
 *
 * @interface KeyPair
 * @property {CryptoKey} publicKey - RSA public key for encryption operations
 * @property {CryptoKey} privateKey - RSA private key for decryption operations
 */
export interface KeyPair {
  publicKey: CryptoKey;
  privateKey: CryptoKey;
}

/**
 * Exported key pair in PEM format for storage/transmission
 *
 * @interface ExportedKeyPair
 * @property {string} publicKeyPem - Public key in PEM format (SPKI encoding)
 * @property {string} privateKeyPem - Private key in PEM format (PKCS#8 encoding)
 */
export interface ExportedKeyPair {
  publicKeyPem: string;
  privateKeyPem: string;
}

/**
 * Generates RSA-OAEP key pair for encryption
 *
 * Creates a new cryptographic key pair using the Web Crypto API. The generated
 * keys are suitable for hybrid encryption schemes where the public key encrypts
 * AES keys and the private key decrypts them.
 *
 * **Key Specifications:**
 * - Algorithm: RSA-OAEP (Optimal Asymmetric Encryption Padding)
 * - Modulus Length: 2048 bits (MVP), upgradeable to 4096 bits
 * - Public Exponent: 65537 (standard value, 0x010001)
 * - Hash Algorithm: SHA-256
 * - Extractable: Yes (allows export to PEM format)
 *
 * **Security Note:**
 * 2048-bit keys are currently considered secure but 4096-bit keys provide
 * longer-term security. Consider upgrading for production environments.
 *
 * @returns {Promise<KeyPair>} Generated RSA key pair
 * @throws {Error} If Web Crypto API is not supported
 * @throws {Error} If key generation fails
 *
 * @example
 * const keyPair = await generateKeyPair();
 * // keyPair.publicKey can be used for encryption
 * // keyPair.privateKey can be used for decryption
 */
export async function generateKeyPair(): Promise<KeyPair> {

  if (!isWebCryptoSupported()) {
    throw new Error('Web Crypto API is not supported in this browser');
  }

  try {
    const keyPair = await window.crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 2048, // MVP: 2048-bit, can upgrade to 4096-bit
        publicExponent: new Uint8Array([1, 0, 1]), // 65537
        hash: 'SHA-256',
      },
      true, // extractable
      ['encrypt', 'decrypt'],
    );


    return {
      publicKey: keyPair.publicKey,
      privateKey: keyPair.privateKey,
    };
  } catch (error) {
    logger.error('Key generation failed', error as Error);
    throw new Error('Failed to generate encryption keys');
  }
}

/**
 * Exports key pair to PEM format for storage
 *
 * Converts Web Crypto API key objects to PEM (Privacy Enhanced Mail) format
 * strings. PEM format is Base64-encoded with header/footer markers, making
 * it suitable for storage in databases or transmission over text-based protocols.
 *
 * **Export Formats:**
 * - Public Key: SPKI (SubjectPublicKeyInfo) format
 * - Private Key: PKCS#8 format
 *
 * **Important:**
 * Private keys are exported in unencrypted form. You MUST encrypt the
 * private key using encryptPrivateKey() before storage.
 *
 * @param {KeyPair} keyPair - Web Crypto API key pair to export
 * @returns {Promise<ExportedKeyPair>} Keys in PEM format
 * @throws {Error} If key export fails
 *
 * @example
 * const keyPair = await generateKeyPair();
 * const exported = await exportKeyPair(keyPair);
 * // exported.publicKeyPem: "-----BEGIN PUBLIC KEY-----\n..."
 * // exported.privateKeyPem: "-----BEGIN PRIVATE KEY-----\n..."
 *
 * @see encryptPrivateKey for securing the private key
 */
export async function exportKeyPair(
  keyPair: KeyPair,
): Promise<ExportedKeyPair> {

  try {
    const publicKeyBuffer = await window.crypto.subtle.exportKey(
      'spki',
      keyPair.publicKey as CryptoKey,
    );
    const privateKeyBuffer = await window.crypto.subtle.exportKey(
      'pkcs8',
      keyPair.privateKey as CryptoKey,
    );

    return {
      publicKeyPem: arrayBufferToPem(publicKeyBuffer, 'PUBLIC KEY'),
      privateKeyPem: arrayBufferToPem(privateKeyBuffer, 'PRIVATE KEY'),
    };
  } catch (error) {
    logger.error('Key export failed', error as Error);
    throw new Error('Failed to export encryption keys');
  }
}

/**
 * Imports key pair from PEM format
 *
 * Reconstructs Web Crypto API key objects from PEM-formatted strings.
 * This is the reverse operation of exportKeyPair().
 *
 * **Expected Formats:**
 * - Public Key: PEM format with SPKI encoding
 * - Private Key: PEM format with PKCS#8 encoding
 *
 * **Note:**
 * If the private key was encrypted using encryptPrivateKey(), it must
 * be decrypted using decryptPrivateKey() BEFORE calling this function.
 *
 * @param {ExportedKeyPair} exported - Keys in PEM format
 * @returns {Promise<KeyPair>} Reconstructed Web Crypto API key pair
 * @throws {Error} If key import fails (invalid format, corrupted data)
 *
 * @example
 * // After retrieving from storage and decrypting
 * const decryptedPrivateKey = await decryptPrivateKey(
 *   storedKeys.encryptedPrivateKey,
 *   password
 * );
 * const keyPair = await importKeyPair({
 *   publicKeyPem: storedKeys.publicKeyPem,
 *   privateKeyPem: decryptedPrivateKey
 * });
 *
 * @see decryptPrivateKey for decrypting encrypted private keys
 */
export async function importKeyPair(
  exported: ExportedKeyPair,
): Promise<KeyPair> {

  try {
    const publicKeyBuffer = pemToArrayBuffer(exported.publicKeyPem);
    const privateKeyBuffer = pemToArrayBuffer(exported.privateKeyPem);

    const publicKey = await window.crypto.subtle.importKey(
      'spki',
      publicKeyBuffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      ['encrypt'],
    );

    const privateKey = await window.crypto.subtle.importKey(
      'pkcs8',
      privateKeyBuffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      true,
      ['decrypt'],
    );


    return { publicKey, privateKey };
  } catch (error) {
    logger.error('Key import failed', error as Error);
    throw new Error('Failed to import encryption keys');
  }
}

/**
 * Encrypts private key with user password for secure storage
 *
 * Protects the private key using password-based encryption before storage.
 * This ensures that even if the storage is compromised, the private key
 * cannot be used without the user's password.
 *
 * **Encryption Algorithm:**
 * 1. Generate random 16-byte salt
 * 2. Generate random 12-byte IV (initialization vector)
 * 3. Derive AES-256 key from password using PBKDF2:
 *    - 100,000 iterations (prevents brute-force attacks)
 *    - SHA-256 hash function
 *    - Salt for uniqueness
 * 4. Encrypt private key PEM with AES-256-GCM
 * 5. Concatenate: salt + IV + encrypted data
 * 6. Base64 encode the result
 *
 * **Security Features:**
 * - PBKDF2 with 100,000 iterations (OWASP recommended minimum)
 * - AES-256-GCM provides both confidentiality and authenticity
 * - Random salt prevents rainbow table attacks
 * - Random IV ensures different ciphertext for same input
 *
 * @param {string} privateKeyPem - Private key in PEM format (unencrypted)
 * @param {string} password - User's password for encryption
 * @returns {Promise<string>} Base64-encoded encrypted private key (salt+IV+ciphertext)
 * @throws {Error} If encryption fails
 *
 * @example
 * const keyPair = await generateKeyPair();
 * const exported = await exportKeyPair(keyPair);
 * const encrypted = await encryptPrivateKey(
 *   exported.privateKeyPem,
 *   'user-strong-password'
 * );
 * // Store encrypted in database or IndexedDB
 *
 * @see decryptPrivateKey for decryption
 */
export async function encryptPrivateKey(
  privateKeyPem: string,
  password: string,
): Promise<string> {

  const encoder = new TextEncoder();
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  // Derive key from password
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );

  const key = await window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt'],
  );

  // Encrypt private key
  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(privateKeyPem),
  );

  // Combine salt + iv + encrypted data
  const combined = new Uint8Array(
    salt.length + iv.length + encrypted.byteLength,
  );
  combined.set(salt, 0);
  combined.set(iv, salt.length);
  combined.set(new Uint8Array(encrypted), salt.length + iv.length);


  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypts private key with user password
 *
 * Recovers the plaintext private key from password-encrypted storage.
 * This is the reverse operation of encryptPrivateKey().
 *
 * **Decryption Process:**
 * 1. Base64 decode the encrypted data
 * 2. Extract salt (first 16 bytes)
 * 3. Extract IV (next 12 bytes)
 * 4. Extract ciphertext (remaining bytes)
 * 5. Derive AES-256 key from password using PBKDF2 (same parameters as encryption)
 * 6. Decrypt ciphertext using AES-256-GCM
 * 7. Return decrypted private key PEM
 *
 * **Security:**
 * - Timing-safe comparison prevents timing attacks
 * - GCM authentication tag prevents tampering
 * - Failed decryption doesn't reveal information about the key
 *
 * @param {string} encryptedData - Base64-encoded encrypted private key (from encryptPrivateKey())
 * @param {string} password - User's password for decryption
 * @returns {Promise<string>} Decrypted private key in PEM format
 * @throws {Error} If password is incorrect or data is corrupted
 *
 * @example
 * // Retrieve from storage
 * const storedKeys = await getStoredKeys(userId);
 * try {
 *   const privateKeyPem = await decryptPrivateKey(
 *     storedKeys.encryptedPrivateKey,
 *     userPassword
 *   );
 *   // Use privateKeyPem with importKeyPair()
 * } catch (error) {
 *   // Handle incorrect password
 *   logger.error('Invalid password');
 * }
 *
 * @see encryptPrivateKey for encryption
 * @see importKeyPair for importing the decrypted key
 */
export async function decryptPrivateKey(
  encryptedData: string,
  password: string,
): Promise<string> {

  const encoder = new TextEncoder();
  const combined = new Uint8Array(
    atob(encryptedData)
      .split('')
      .map((c) => c.charCodeAt(0)),
  );

  // Extract salt, iv, and encrypted data
  const salt = combined.slice(0, 16);
  const iv = combined.slice(16, 28);
  const encrypted = combined.slice(28);

  // Derive key from password
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );

  const key = await window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  );

  // Decrypt private key
  try {
    const decrypted = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted,
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch (error) {
    logger.error('Decryption failed', error as Error);
    throw new Error('Failed to decrypt private key - invalid password');
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if Web Crypto API is supported in the current environment
 *
 * @returns {boolean} True if Web Crypto API is available
 * @private
 */
function isWebCryptoSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.crypto !== 'undefined' &&
    typeof window.crypto.subtle !== 'undefined'
  );
}

/**
 * Convert ArrayBuffer to PEM format string
 *
 * @param {ArrayBuffer} buffer - Binary key data
 * @param {string} label - PEM label (e.g., "PUBLIC KEY", "PRIVATE KEY")
 * @returns {string} PEM-formatted string
 * @private
 */
function arrayBufferToPem(buffer: ArrayBuffer, label: string): string {
  const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
  const pem = base64.match(/.{1,64}/g)?.join('\n') || base64;
  return `-----BEGIN ${label}-----\n${pem}\n-----END ${label}-----`;
}

/**
 * Convert PEM format string to ArrayBuffer
 *
 * @param {string} pem - PEM-formatted key string
 * @returns {ArrayBuffer} Binary key data
 * @private
 */
function pemToArrayBuffer(pem: string): ArrayBuffer {
  const base64 = pem
    .replace(/-----BEGIN [A-Z ]+-----/, '')
    .replace(/-----END [A-Z ]+-----/, '')
    .replace(/\s/g, '');

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}
